import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { CreditCard, Lock, Plus, Ticket, Trash2 } from "lucide-react"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { ApiError } from "@/core/api"
import {
  connectStatusQueryOptions,
  createTicket,
  deleteTicket,
  dollarsToCents,
  eventBillingStatusQueryOptions,
  formatMoney,
  ordersQueryOptions,
  startConnectOnboarding,
  ticketingActive,
  ticketsKey,
  ticketsQueryOptions,
  updateTicket,
  type ConnectStatus,
  type TicketType,
} from "@/core/payments"
import {
  Card,
  Field,
  GhostButton,
  PrimaryButton,
  Select,
  SettingsPage,
  TextInput,
  Toggle,
} from "@/components/settings/primitives"
import { cn } from "@/core/utils"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/ticketing")({
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(connectStatusQueryOptions()).catch(() => undefined)
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient.ensureQueryData(eventBillingStatusQueryOptions(eventId)).catch(() => undefined)
      void context.queryClient.ensureQueryData(ticketsQueryOptions(eventId)).catch(() => undefined)
      void context.queryClient.ensureQueryData(ordersQueryOptions(eventId)).catch(() => undefined)
    })
  },
  component: TicketingView,
})

function TicketingView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<TicketType | "new" | null>(null)

  const connectQ = useQuery(connectStatusQueryOptions())
  const billingQ = useQuery({ ...eventBillingStatusQueryOptions(eventId ?? ""), enabled: !!eventId })
  const ticketsQ = useQuery({ ...ticketsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const tickets = ticketsQ.data ?? []

  // Ticket-tier setup only needs the Ticket Sales package. Stripe Connect gates
  // public checkout, not configuration, so organizers can build ticket types
  // before finishing onboarding.
  const ticketingEnabled = ticketingActive(billingQ.data)
  const connectReady = !!connectQ.data?.connected && !!connectQ.data?.chargesEnabled
  const canManageTickets = ticketingEnabled
  const canSell = ticketingEnabled && connectReady
  const invalidate = () => void qc.invalidateQueries({ queryKey: ticketsKey(eventId ?? "") })

  return (
    <SettingsPage
      title="Tickets"
      description="Sell paid tickets on your public event page. Payments go straight to your Stripe account."
      actions={
        canManageTickets ? (
          <PrimaryButton onClick={() => setEditing("new")} disabled={!eventId}>
            <Plus className="mr-1 h-4 w-4" /> Add ticket
          </PrimaryButton>
        ) : undefined
      }
    >
      {billingQ.isSuccess && !ticketingEnabled && <TicketingLockedCard />}

      {ticketingEnabled && <ConnectCard status={connectQ.data} loading={connectQ.isLoading} />}

      {/* The Attend platform fee is set by a platform admin, never the organizer. */}

      {canManageTickets && editing && eventId && (
        <TicketForm
          key={editing === "new" ? "new" : editing.id}
          eventId={eventId}
          ticket={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            invalidate()
          }}
        />
      )}

      {canManageTickets && (
        <Card
          title="Ticket types"
          description={canSell ? undefined : "Configure tickets now. Connect Stripe before attendees can buy them."}
        >
          {ticketsQ.isLoading && tickets.length === 0 ? (
            <div className="space-y-2">
              {[0, 1].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-xl bg-plum-100/60 dark:bg-white/5" />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex items-center gap-2 py-2 text-[13px] text-plum-700/55 dark:text-plum-200/40">
              <Ticket className="h-4 w-4" />
              No ticket types yet — add one to configure paid registration.
            </div>
          ) : (
            <div className="divide-y divide-plum-200/50 dark:divide-white/10">
              {tickets.map((t) => (
                <TicketRow
                  key={t.id}
                  ticket={t}
                  onEdit={() => setEditing(t)}
                  onChanged={invalidate}
                />
              ))}
            </div>
          )}
        </Card>
      )}

      {canSell && <OrdersCard eventId={eventId ?? null} />}
    </SettingsPage>
  )
}

// ── Ticketing not enabled ─────────────────────────────────────────────────────

// Shown when the Ticket Sales package isn't active (paid/comped) on this event.
// The organizer can't self-serve it — a platform admin comps or bills it — so we
// point them at their rep rather than offering a button the backend would 402.
function TicketingLockedCard() {
  return (
    <Card title="Ticketing">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 grid h-8 w-8 place-items-center rounded-lg bg-amber-500/12 text-amber-600 dark:text-amber-400">
          <Lock className="h-4 w-4" />
        </span>
        <div>
          <div className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">
            Ticketing isn't enabled for this event
          </div>
          <div className="text-[12px] text-plum-700/55 dark:text-plum-200/40">
            Paid tickets &amp; registration are part of the Ticket Sales package. Contact your Attend
            rep to turn it on for this event.
          </div>
        </div>
      </div>
    </Card>
  )
}

// ── Connect status ──────────────────────────────────────────────────────────

function ConnectCard({ status, loading }: { status?: ConnectStatus; loading: boolean }) {
  const connect = useMutation({
    mutationFn: startConnectOnboarding,
    onSuccess: (r) => {
      window.open(r.url, "_blank", "noopener,noreferrer")
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't start Stripe onboarding"),
  })

  if (loading) {
    return <Card><div className="h-10 animate-pulse rounded-lg bg-plum-100/60 dark:bg-white/5" /></Card>
  }

  const ready = !!status?.connected && !!status?.chargesEnabled

  return (
    <Card title="Payments">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span
            className={cn(
              "mt-0.5 grid h-8 w-8 place-items-center rounded-lg",
              ready
                ? "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400"
                : "bg-amber-500/12 text-amber-600 dark:text-amber-400",
            )}
          >
            <CreditCard className="h-4 w-4" />
          </span>
          <div>
            <div className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">
              {ready ? "Stripe connected" : status?.connected ? "Finish Stripe setup" : "Connect Stripe to sell tickets"}
            </div>
            <div className="text-[12px] text-plum-700/55 dark:text-plum-200/40">
              {ready
                ? "You're set up to take payments — funds land directly with you."
                : status?.connected
                  ? "Your account needs a few more details before it can take charges."
                  : "Link your own Stripe account — funds land directly with you. Takes a couple of minutes."}
            </div>
          </div>
        </div>
        {!ready && (
          <PrimaryButton onClick={() => connect.mutate()} disabled={connect.isPending}>
            {connect.isPending ? "Opening…" : status?.connected ? "Continue setup" : "Connect Stripe"}
          </PrimaryButton>
        )}
      </div>
    </Card>
  )
}

// ── Ticket row ──────────────────────────────────────────────────────────────

function TicketRow({
  ticket,
  onEdit,
  onChanged,
}: {
  ticket: TicketType
  onEdit: () => void
  onChanged: () => void
}) {
  const del = useMutation({
    mutationFn: () => deleteTicket(ticket.id),
    onSuccess: () => {
      toast.success("Ticket deleted")
      onChanged()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Delete failed"),
  })

  const sold = ticket.quantitySold
  const cap = ticket.quantityTotal
  const remaining = cap == null ? null : Math.max(0, cap - sold - ticket.quantityReserved)

  return (
    <div className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <button onClick={onEdit} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
            {ticket.name}
          </span>
          {ticket.active !== 1 && (
            <span className="rounded bg-plum-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-plum-700/70 dark:bg-white/8 dark:text-plum-200/50">
              Inactive
            </span>
          )}
        </div>
        <div className="truncate text-[12px] text-plum-700/55 dark:text-plum-200/40">
          {formatMoney(ticket.priceCents, ticket.currency)}
          {" · "}
          {sold} sold
          {cap == null ? " · unlimited" : ` · ${remaining} left of ${cap}`}
        </div>
      </button>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          onClick={() => {
            if (confirm(`Delete "${ticket.name}"?`)) del.mutate()
          }}
          className="grid h-7 w-7 place-items-center rounded-md text-plum-700/40 transition hover:bg-rose-50 hover:text-rose-600 dark:text-plum-200/30 dark:hover:bg-rose-500/10"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// ── Ticket form ─────────────────────────────────────────────────────────────

function TicketForm({
  eventId,
  ticket,
  onClose,
  onSaved,
}: {
  eventId: string
  ticket: TicketType | null
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(ticket?.name ?? "")
  const [description, setDescription] = useState(ticket?.description ?? "")
  const [price, setPrice] = useState(ticket ? (ticket.priceCents / 100).toFixed(2) : "")
  const [currency, setCurrency] = useState(ticket?.currency ?? "usd")
  const [capacity, setCapacity] = useState(ticket?.quantityTotal != null ? String(ticket.quantityTotal) : "")
  const [active, setActive] = useState(ticket ? ticket.active === 1 : true)

  const cents = dollarsToCents(price)
  const valid = name.trim().length > 0 && cents != null && cents > 0

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        // Backend description is .optional() (undefined), NOT .nullable() — send
        // undefined when empty so the ticket-create validation passes.
        description: description.trim() || undefined,
        priceCents: cents!,
        currency,
        quantityTotal: capacity.trim() === "" ? null : Math.max(0, Number(capacity)),
        active,
      }
      if (ticket) await updateTicket(ticket.id, body)
      else await createTicket(eventId, body)
    },
    onSuccess: () => {
      toast.success(ticket ? "Ticket saved" : "Ticket added")
      onSaved()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Save failed"),
  })

  return (
    <Card title={ticket ? "Edit ticket" : "New ticket"}>
      <Field label="Name">
        <TextInput autoFocus value={name} placeholder="General Admission" onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description" hint="Optional — shown under the ticket name at checkout.">
        <TextInput value={description} placeholder="Includes welcome dinner" onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="grid gap-4 @[480px]:grid-cols-2">
        <Field label="Price">
          <TextInput
            inputMode="decimal"
            value={price}
            placeholder="0.00"
            onChange={(e) => setPrice(e.target.value)}
            className={price.trim() && (cents == null || cents <= 0) ? "border-rose-400" : ""}
          />
        </Field>
        <Field label="Currency">
          <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="usd">USD</option>
            <option value="eur">EUR</option>
            <option value="gbp">GBP</option>
            <option value="cad">CAD</option>
            <option value="aud">AUD</option>
          </Select>
        </Field>
      </div>
      <Field label="Capacity" hint="How many to sell. Leave blank for unlimited.">
        <TextInput
          type="number"
          min={0}
          inputMode="numeric"
          placeholder="Unlimited"
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
        />
      </Field>
      <Toggle
        label="Active"
        description="Inactive tickets stay hidden from the public page."
        checked={active}
        onChange={setActive}
      />
      <div className="flex items-center justify-end gap-2 pt-1">
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton onClick={() => valid && !save.isPending && save.mutate()} disabled={!valid || save.isPending}>
          {save.isPending ? "Saving…" : ticket ? "Save" : "Add ticket"}
        </PrimaryButton>
      </div>
    </Card>
  )
}

// ── Orders ──────────────────────────────────────────────────────────────────

function OrdersCard({ eventId }: { eventId: string | null }) {
  const q = useQuery({ ...ordersQueryOptions(eventId ?? ""), enabled: !!eventId })
  const orders = q.data ?? []

  return (
    <Card title="Orders" description="Paid and refunded ticket purchases.">
      {q.isLoading ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-plum-100/60 dark:bg-white/5" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="py-2 text-[13px] text-plum-700/55 dark:text-plum-200/40">No orders yet.</div>
      ) : (
        <div className="divide-y divide-plum-200/50 dark:divide-white/10">
          {orders.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-plum-900 dark:text-plum-50">
                  {o.buyerName || o.buyerEmail}
                </div>
                <div className="truncate text-[12px] text-plum-700/55 dark:text-plum-200/40">{o.buyerEmail}</div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-[13px] font-medium tabular-nums text-plum-900 dark:text-plum-50">
                  {formatMoney(o.amountCents, o.currency)}
                </span>
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                    o.status === "paid"
                      ? "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400"
                      : o.status === "refunded"
                        ? "bg-plum-100 text-plum-700/70 dark:bg-white/8 dark:text-plum-200/50"
                        : "bg-amber-500/12 text-amber-600 dark:text-amber-400",
                  )}
                >
                  {o.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
